import React, { useEffect, useState } from "react";
import Card from "components/card";
import Offcanvas from "components/Offcanvas";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { getFaqsService } from "api/services/cmsServices";
import { updateFaqs } from "api/services/cmsServices";
import { toast } from "react-toastify";
import { createFaqs } from "api/services/cmsServices";
import ConfirmationModal from "components/ConfirmationModal";
import { deleteFaqs } from "api/services/cmsServices";

const initialFaqs = [
    { question: "What is your return policy?", answer: "You can return any item within 30 days." },
    { question: "How do I contact support?", answer: "Email us at support@example.com." },
];

const FAQ = () => {
    const [faqs, setFaqs] = useState(initialFaqs);
    const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);
    const [form, setForm] = useState({ question: "", answer: "" });
    const [editIndex, setEditIndex] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    useEffect(() => {
        getFaqs();
    }, []);

    const getFaqs = async () => {
        const response = await getFaqsService();
        if (response.status === 200) {
            setFaqs(response.data.data);
        }
    };

    const handleOpen = () => {
        setForm({ question: "", answer: "" });
        setEditIndex(null);
        setIsOffcanvasOpen(true);
    };
    const handleClose = () => setIsOffcanvasOpen(false);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            if (editIndex !== null) {
                // Edit mode
                let payload = {
                    question: form.question,
                    answer: form.answer,
                    id: editIndex,
                }
                const response = await updateFaqs(payload);
                if (response.status === 200) {
                    toast.success("FAQ updated successfully");
                    getFaqs();
                } else {
                    toast.error("Failed to update FAQ");
                }
            } else {
                let payload = {
                    question: form.question,
                    answer: form.answer,
                }
                const response = await createFaqs(payload);
                if (response.status === 201) {
                    toast.success("FAQ created successfully");
                    getFaqs();
                } else {
                    toast.error("Failed to create FAQ");
                }
            }
        } catch (error) {
            console.log(error)
            toast.error(error.response?.data?.error || "Something went wrong");
        }
        setIsOffcanvasOpen(false);
    };

    const handleEdit = (id) => {
        let faq = faqs.find(faq => faq?._id === id);
        setForm(faq);
        setEditIndex(faq?._id);
        setIsOffcanvasOpen(true);
    };

    const handleDelete = (id) => {
        setDeleteId(id);
        setIsConfirmOpen(true);
    };

    const confirmDelete = async () => {
        setFaqs(faqs.filter(faq => faq?._id !== deleteId));
        setIsConfirmOpen(false);
        setDeleteId(null);
        let response = await deleteFaqs(deleteId);
        if (response.status === 200) {
            toast.success("FAQ deleted successfully");
            getFaqs();
        } else {
            toast.error("Failed to delete FAQ");
        }
    };

    const cancelDelete = () => {
        setIsConfirmOpen(false);
        setDeleteId(null);
    };

    return (
        <Card extra="w-full h-full sm:overflow-auto px-6 pb-5 mt-5">
            <header className="relative flex flex-col gap-4 pt-4">
                <div className="flex items-center justify-between">
                    <div className="text-xl font-bold text-navy-700 dark:text-white">FAQs</div>
                    <button
                        onClick={handleOpen}
                        className="rounded-md bg-brand-500 px-4 py-1.5 text-sm font-semibold text-white shadow transition hover:bg-brand-600"
                    >
                        + Add FAQ
                    </button>
                </div>
            </header>
            <div className="mt-6 overflow-x-scroll xl:overflow-x-hidden">
                <table className="w-full">
                    <thead>
                        <tr className="!border-px !border-gray-400">
                            <th className="border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-start text-sm font-bold text-gray-600 dark:text-white">QUESTION</th>
                            <th className="border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-start text-sm font-bold text-gray-600 dark:text-white">ANSWER</th>
                            <th className="border-b-[1px] border-gray-200 pb-2 pr-4 pt-4 text-center text-sm font-bold text-gray-600 dark:text-white w-20">ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {faqs.map((faq, idx) => (
                            <tr key={faq?._id || idx}>
                                <td className="min-w-[200px] py-3 pr-4">
                                    <p className="text-sm font-bold text-navy-700 dark:text-white">{faq.question}</p>
                                </td>
                                <td className="min-w-[300px] py-3 pr-4">
                                    <p className="text-sm text-navy-700 dark:text-white">{faq.answer}</p>
                                </td>
                                <td className="py-3 pr-4 text-center">
                                    <button
                                        onClick={() => handleEdit(faq?._id)}
                                        className="inline-flex items-center justify-center rounded p-1 text-brand-500 hover:bg-brand-50 dark:hover:bg-white/10 mr-2"
                                        title="Edit"
                                    >
                                        <FiEdit />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(faq?._id)}
                                        className="inline-flex items-center justify-center rounded p-1 text-red-500 hover:bg-red-50 dark:hover:bg-white/10"
                                        title="Delete"
                                    >
                                        <FiTrash2 />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Offcanvas isOpen={isOffcanvasOpen} onClose={handleClose} title={editIndex !== null ? "Edit FAQ" : "Add FAQ"}>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div>
                        <label className="block text-sm font-medium text-navy-700 dark:text-white mb-1">Question</label>
                        <input
                            type="text"
                            name="question"
                            value={form.question}
                            onChange={handleChange}
                            required
                            className="w-full rounded border px-3 py-2 text-sm dark:!bg-white/5"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-navy-700 dark:text-white mb-1">Answer</label>
                        <textarea
                            name="answer"
                            value={form.answer}
                            onChange={handleChange}
                            required
                            rows={4}
                            className="w-full rounded border px-3 py-2 text-sm dark:!bg-white/5"
                        />
                    </div>
                    <button
                        type="submit"
                        className="rounded-md bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow transition hover:bg-brand-600"
                    >
                        {editIndex !== null ? "Update FAQ" : "Add FAQ"}
                    </button>
                </form>
            </Offcanvas>
            <ConfirmationModal
                isOpen={isConfirmOpen}
                onClose={cancelDelete}
                onConfirm={confirmDelete}
                title="Delete FAQ?"
                description="Are you sure you want to delete this FAQ? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
            />
        </Card>
    );
};

export default FAQ;
